import type { IncidentCategory } from '@piccolohealth/pbs-common';
import { Select, type SelectOption, type SelectProps } from '@piccolohealth/ui';
import React from 'react';
import { IncidentCategoryTag } from '../../features/incidents/IncidentCategoryTag';
import { useIncidentCategoriesQuery } from '../../graphql/hooks/useIncidentCategoriesQuery';
import { useAppContext } from '../../hooks/useAppContext';

export interface IncidentCategoryChooserProps
  extends Omit<
    SelectProps<IncidentCategory, unknown>,
    'value' | 'onChange' | 'options' | 'selectedValues'
  > {
  value: string | null;
  onChange: (value: string | undefined) => void;
}

export const IncidentCategoryChooser = (props: IncidentCategoryChooserProps) => {
  const { value, onChange, ...rest } = props;
  const { organization } = useAppContext();

  const { isLoading, data } = useIncidentCategoriesQuery({
    organizationId: organization.id,
  });

  const incidentCategories = (data?.organization?.incidentCategories ?? []) as IncidentCategory[];

  const options: SelectOption<IncidentCategory>[] = incidentCategories.map((category) => ({
    label: category.name,
    value: category.id,
    raw: category,
    color: category.color,
  }));

  const onChangePrime = React.useCallback(
    (option: SelectOption<IncidentCategory> | null) => {
      onChange(option?.value ?? undefined);
    },
    [onChange],
  );

  const selectedValue = React.useMemo(() => {
    return options.find((option) => option.value === value) ?? null;
  }, [options, value]);

  return (
    <Select
      isLoading={isLoading}
      value={selectedValue}
      options={options}
      onChange={onChangePrime}
      placeholder='Select a category'
      components={{
        Value: (props) => <IncidentCategoryTag category={props.option.raw} size='md' />,

        Option: (props) => {
          return <IncidentCategoryTag category={props.option.raw} size='md' />;
        },
      }}
      {...rest}
    />
  );
};
